/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { getAccessToken } from '@/libs/firebaseAuth';
import { getBaseUrl } from '@/libs/env';
import { handleAuthorizationError } from '@/libs/apiErrorHandler';
import router from '@/router';
import { error } from '@/libs/notification';

export type ActionKey = 'NONE' | 'VIEW' | 'EDIT' | 'CRUD';

export type PermissionItem = {
  screenId: number;
  actionKey: ActionKey;
};

export type RolePermissionScreen = {
  id: number;
  code: string;
  name: string;
  childs: Array<{
    id: number;
    code: string;
    name: string;
  }>;
};

export type RolePermissionRole = {
  id: number;
  code: string;
  name: string;
  level: 1 | 2 | 3;
  managedChildRoleIds: number[];
  /** Parent manager role ids when `level` is 1 (担当者). */
  parentRoleIds?: number[];
  canDeleted: boolean;
  permissionConfig: PermissionItem[];
  /** Post-login redirect path in effect after approve (login uses this). */
  homePath?: string | null;
  /** Draft home path until approve; null when none pending. */
  homePathDraft?: string | null;
};

export type RolePermissionDetail = {
  id: number;
  code: string;
  name: string;
  level: 1 | 2 | 3;
  managedChildRoleIds: number[];
  parentRoleIds?: number[];
  canDeleted: boolean;
  permissionsMain: PermissionItem[];
  permissionsDraft: PermissionItem[];
  /** Post-login redirect path in effect after approve. */
  homePath?: string | null;
  /** Draft home path until approve; null when none pending. */
  homePathDraft?: string | null;
};

export type RolePermissionEditHistoryItem = {
  id: number;
  roleId: number;
  at: number;
  action: 'create' | 'update' | 'approve' | 'delete';
  editorUserId: number | null;
  roleCode: string;
  roleName: string;
  editor: string | null;
  diffPayload: Record<string, unknown> | null;
};

type RolePermissionState = {
  screens: RolePermissionScreen[];
  roles: RolePermissionRole[];
  roleDetail: RolePermissionDetail | null;
  editHistoryItems: RolePermissionEditHistoryItem[];
  editHistoryNext: number | null;
};

const getAuthConfig = async () => ({
  headers: { auth: await getAccessToken() }
});
const pickArray = <T>(data: unknown, key: string): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as any)[key])) {
    return (data as any)[key] as T[];
  }
  return [];
};

export default {
  namespaced: true,
  state: {
    screens: [],
    roles: [],
    roleDetail: null,
    editHistoryItems: [],
    editHistoryNext: null
  } as RolePermissionState,
  mutations: {
    setScreens(state: RolePermissionState, payload: RolePermissionScreen[]) {
      state.screens = payload;
    },
    setRoles(state: RolePermissionState, payload: RolePermissionRole[]) {
      state.roles = payload;
    },
    setRoleDetail(
      state: RolePermissionState,
      payload: RolePermissionDetail | null
    ) {
      state.roleDetail = payload;
    },
    setEditHistory(
      state: RolePermissionState,
      payload: { items: RolePermissionEditHistoryItem[]; next: number | null }
    ) {
      state.editHistoryItems = payload.items;
      state.editHistoryNext = payload.next;
    },
    appendEditHistory(
      state: RolePermissionState,
      payload: { items: RolePermissionEditHistoryItem[]; next: number | null }
    ) {
      state.editHistoryItems = [...state.editHistoryItems, ...payload.items];
      state.editHistoryNext = payload.next;
    }
  },
  actions: {
    async fetchScreens(context: any) {
      try {
        const response = await axios.get(
          `${getBaseUrl()}/screens`,
          await getAuthConfig()
        );
        const screens = pickArray<RolePermissionScreen>(
          response.data,
          'screens'
        );
        context.commit('setScreens', screens);
        return screens;
      } catch (e: any) {
        if (e.response.status === 401) {
          await handleAuthorizationError();
        } else if (e.response.status < 500) {
          error(e.response.status);
          router.push('/top');
        } else {
          router.push('/error/server');
        }
      }
    },
    async fetchRoles(context: any) {
      try {
        const response = await axios.get(
          `${getBaseUrl()}/roles`,
          await getAuthConfig()
        );
        const roles = pickArray<RolePermissionRole>(response.data, 'roles');
        context.commit('setRoles', roles);
        return roles;
      } catch (e: any) {
        if (e.response.status === 401) {
          await handleAuthorizationError();
        } else if (e.response.status < 500) {
          error(e.response.status);
          router.push('/top');
        } else {
          router.push('/error/server');
        }
      }
    },
    async fetchRoleDetail(context: any, roleId: number) {
      try {
        const response = await axios.get(
          `${getBaseUrl()}/roles/${roleId}`,
          await getAuthConfig()
        );
        context.commit('setRoleDetail', response.data ?? null);
        return response.data;
      } catch (e: any) {
        if (e.response.status === 401) {
          await handleAuthorizationError();
        } else if (e.response.status < 500) {
          error(e.response.status);
          router.push('/top');
        } else {
          router.push('/error/server');
        }
        throw e;
      }
    },
    async createRole(context: any, payload: Record<string, unknown>) {
      try {
        const response = await axios.post(
          `${getBaseUrl()}/roles`,
          payload,
          await getAuthConfig()
        );
        return response.data;
      } catch (e: any) {
        if (e.response.status === 401) {
          await handleAuthorizationError();
        } else if (e.response.status < 500) {
          error(e.response.status);
          throw e;
        } else {
          router.push('/error/server');
        }
      }
    },
    async updateRoleDraft(
      context: any,
      payload: { roleId: number; body: Record<string, unknown> }
    ) {
      try {
        const response = await axios.patch(
          `${getBaseUrl()}/roles/${payload.roleId}`,
          payload.body,
          await getAuthConfig()
        );
        context.commit('setRoleDetail', response.data ?? null);
        return response.data;
      } catch (e: any) {
        if (e.response.status === 401) {
          await handleAuthorizationError();
        } else if (e.response.status < 500) {
          error(e.response.status);
          router.push('/top');
        } else {
          router.push('/error/server');
        }
      }
    },
    async approveRole(
      context: any,
      payload: { roleId: number; body?: { comment?: string } }
    ) {
      try {
        const response = await axios.post(
          `${getBaseUrl()}/roles/${payload.roleId}/approve`,
          payload.body ?? {},
          await getAuthConfig()
        );
        context.commit('setRoleDetail', response.data ?? null);
        return response.data;
      } catch (e: any) {
        if (e?.response?.status === 401) {
          await handleAuthorizationError();
        }
        throw e;
      }
    },
    async deleteRole(context: any, roleId: number) {
      try {
        await axios.delete(
          `${getBaseUrl()}/roles/${roleId}`,
          await getAuthConfig()
        );
      } catch (e: any) {
        if (e?.response?.status === 401) {
          await handleAuthorizationError();
        }
        throw e;
      }
    },
    async fetchEditHistory(
      context: any,
      payload: { roleId?: number; next?: number; append?: boolean } = {}
    ) {
      try {
        const params: Record<string, number> = {};
        if (typeof payload.roleId === 'number') params.roleId = payload.roleId;
        if (typeof payload.next === 'number') params.next = payload.next;

        let response: any;
        try {
          response = await axios.get(`${getBaseUrl()}/edit-history`, {
            ...(await getAuthConfig()),
            params
          });
        } catch (e: any) {
          // Fallback for older mock path.
          if (e?.response?.status === 404) {
            response = await axios.get(
              `${getBaseUrl()}/rolePermissionEditHistory`,
              {
                ...(await getAuthConfig()),
                params
              }
            );
          } else {
            throw e;
          }
        }

        const result = {
          items: response.data?.items ?? [],
          next: response.data?.next ?? null
        };

        if (payload.append) {
          context.commit('appendEditHistory', result);
        } else {
          context.commit('setEditHistory', result);
        }
        return result;
      } catch (e: any) {
        if (e?.response?.status === 401) {
          await handleAuthorizationError();
        }
        throw e;
      }
    }
  },
  getters: {
    screens(state: RolePermissionState) {
      return state.screens;
    },
    roles(state: RolePermissionState) {
      return state.roles;
    },
    roleDetail(state: RolePermissionState) {
      return state.roleDetail;
    },
    editHistoryItems(state: RolePermissionState) {
      return state.editHistoryItems;
    },
    editHistoryNext(state: RolePermissionState) {
      return state.editHistoryNext;
    }
  }
};
